import { setShowBalances } from "@/modules/dashboard/state";
import { LoanType } from "@/modules/loan/common/enum";
import type { ILoan } from "@/modules/loan/common/types";
import { useGetLoansQuery } from "@/modules/loan/services";
import { AppPaths } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { PageWidgetTemplate } from "@/shared/components";
import { useAppDispatch, useShowBalance } from "@/shared/hooks";
import { setActiveLoanId, setLoanActiveTab } from "@/shared/state/common";
import classNames from "classnames";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LoansItem } from "./LoansItem";

interface Props {
  limit?: number;
  loanType?: string;
  loanId?: string;
}

export const Loans = ({
  limit,
  loanId,
  loanType,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { handleShowBalances } = useShowBalance();

  const {
    data: loans,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetLoansQuery({
    pageSize: limit,
    loanTypes: [LoanType[loanType as keyof typeof LoanType]],
  }, {
    refetchOnMountOrArgChange: true,
  });

  const currentDate = new Date();

  const onShowDetails = (loan: ILoan) => {
    dispatch(setLoanActiveTab(loan.loanType));
    dispatch(setActiveLoanId(loan.id));
    if (!location.pathname.includes(AppPaths.Loans)) {
      navigate(AppPaths.Loans);
    }
  };

  useEffect(() => {
    if (loans?.length) {
      const showBalances = Array(loans?.length).fill(false);
      dispatch(setShowBalances(showBalances));
    }
  }, [loans]);

  return (
    <PageWidgetTemplate
      isFetching={isFetching}
      isError={isError}
      error={error as IError}
      refetch={refetch}
      isEmpty={!loans?.length}
    >
      {loans?.map((loan, i) => (
        <div key={loan.id} onClick={() => onShowDetails(loan)}>
          <LoansItem
            {...loan}
            index={i}
            className={classNames({ "selected": loanId === `${loan.id}` })}
            currentDate={currentDate}
            handleShowBalances={handleShowBalances}
          />
        </div>
      ))}
    </PageWidgetTemplate>
  );
};
